import * as React from 'react'
import { createContext, PropsWithChildren, ReactElement, useEffect, useMemo, useRef, useState } from 'react'
import { AxiosError } from 'axios'
import {
    AccountInfo,
    AuthenticationResult,
    BrowserCacheLocation,
    IPublicClientApplication,
    LogLevel,
    PublicClientApplication,
} from '@azure/msal-browser'
import { useMount } from '@fluentui/react-hooks'
import { asBoolean } from '../helpers/functionHelper'
import { isOfficeInitialized } from '../helpers/outlookHelper'
import { jwtDecode, JwtPayload } from 'jwt-decode'
import { DateTime } from 'luxon'
import useAxios from '../hooks/useAxios'
import { ServerAuthorizationTokenResponse } from '@azure/msal-common/dist/response/ServerAuthorizationTokenResponse'
import { Role } from '../hooks/useRoles'
import { Destination, graphBaseUrl } from './AxiosContext'
import useLocalStorage from '../hooks/useLocalStorage'
import { flushLoginTelemetry, markInitialLoginComplete, markTokensFetched, trackLoginFailure, trackLoginPhase } from '../helpers/loginTelemetry'

export const AUTHENTICATION_SCOPES: string[] = [`${process.env.BOOTSTRAP_API_URI}/access_as_user`, 'openid', 'email', 'profile']

// An access token is only accepted if it still has more than this much life left (see `isTokenNotExpired`).
const ACCESS_TOKEN_EXPIRY_BUFFER_MS = 60_000
// A bootstrap token is only accepted if it still has more than this much life left. It must stay strictly
// larger than ACCESS_TOKEN_EXPIRY_BUFFER_MS: an access token derived (on-behalf-of) from the bootstrap token
// inherits an EntraID expiry no later than the bootstrap's, so a bootstrap that is "valid enough to derive
// from" must leave the resulting access token comfortably above the 60s acceptance buffer even after the
// acquisition round-trip and clock skew. Otherwise we'd accept a bootstrap that can only yield an
// already-expired access token, spinning the tokens-acquisition effect forever.
const BOOTSTRAP_EXPIRY_BUFFER_MS = 90_000

export type AzureTokenPayload = OfficeTokenPayload & {
    roles: Role[]
}

export type OfficeTokenPayload = {
    oid: string // UUID of user
    name: string // User's display name
    preferred_username: string // E-mail address of user
    unique_name: string // E-mail address of user
    upn: string // unified name, most often the e-mail address
    scp: string // Space-separated scopes
    tid: string // Tenant ID
    uti: string // Unique, per-token identifier that is case-sensitive.
} & JwtPayload

export type Host = {
    RowKey: string
    Name: string
    Backend: string
    ClientId: string
    ClientPath: string
    DriveId: string
    ReplicationMail: string
    tokens: Tokens
}

export enum SecurityAuthority {
    Hammock = 'hammock', // Connect to HAMMOCK in the user's tenant.
    SharePoint = 'sharepoint', // Connect to MS Graph in the Hammock.nl tenant.
    Exchange = 'exchange', // Connect to MS Graph in the user's tenant.
    WebPubSub = 'webpubsub', // Connect to A Web Pub Sub socket in the user's hub.
}

export const DestinationAuthorityMap = new Map<Destination, SecurityAuthority>([
    [Destination.Hammock, SecurityAuthority.Hammock],
    [Destination.Exchange, SecurityAuthority.Exchange],
    [Destination.ReplicationEmail, SecurityAuthority.Exchange],
    [Destination.Email, SecurityAuthority.Exchange],
    [Destination.PrivateEmail, SecurityAuthority.Exchange],
    [Destination.SharePoint, SecurityAuthority.SharePoint],
    [Destination.Documents, SecurityAuthority.SharePoint],
])

type PartialServerAuthorizationTokenResponse = Pick<ServerAuthorizationTokenResponse, 'access_token' | 'refresh_token' | 'token_type'>

export type WebPubSubToken = PartialServerAuthorizationTokenResponse & {
    hub: string
    url: string
    issued_at: string
    expires_at: string
}

export type Tokens = {
    [SecurityAuthority.Hammock]?: ServerAuthorizationTokenResponse
    [SecurityAuthority.SharePoint]?: ServerAuthorizationTokenResponse
    [SecurityAuthority.Exchange]?: ServerAuthorizationTokenResponse
    [SecurityAuthority.WebPubSub]?: WebPubSubToken
}

export type AuthorizationContextType = {
    identified: boolean
    authenticated: boolean
    user?: AccountInfo
    mailbox?: string
    bootstrapToken?: string
    hosts?: Host[]
    host?: Host
    setHost: (host: Host) => void
    tokens?: Tokens
    roles?: Role[]
    /**
     * Stable identifier for the (user, environment) tuple. Defined only when tokens for the currently
     * selected host have actually loaded. `undefined` while a host switch is in flight, while tokens
     * are expired, before initial token load, and after logout. Use this as a `useEffect` dep to
     * reload per-environment caches (e.g. `/global`) — string identity is stable across token refreshes.
     */
    environmentKey?: string
    /**
     * Bumps every time `tokens` is replaced with a fresh server response. Use this as a `useEffect`
     * dep if you specifically need to react to new tokens (re-decoding claims, refreshing a socket
     * that embeds the token). Most consumers should depend on `environmentKey` instead.
     */
    tokensRevision: number
    updateTokens: (authority: SecurityAuthority) => Promise<ServerAuthorizationTokenResponse | WebPubSubToken>
    getBaseUrl: (destination: Destination) => string | undefined
    reset: () => void
}

const AuthorizationContext = createContext<AuthorizationContextType>({
    identified: false,
    authenticated: false,
    user: undefined,
    mailbox: undefined,
    bootstrapToken: undefined,
    host: undefined,
    setHost: () => {},
    tokens: undefined,
    roles: undefined,
    environmentKey: undefined,
    tokensRevision: 0,
    updateTokens: () => Promise.resolve<ServerAuthorizationTokenResponse | WebPubSubToken>(undefined),
    getBaseUrl: () => graphBaseUrl,
    reset: () => {
        clearLocalStorageGlobals()
        window.location.reload()
    },
})

type AuthenticationStorageDto = Pick<AuthorizationContextType, 'identified' | 'authenticated' | 'user' | 'mailbox'>
type AuthorizationHostStorageDto = Pick<AuthorizationContextType, 'bootstrapToken' | 'hosts' | 'host'>
type AuthorizationTokensStorageDto = Pick<AuthorizationContextType, 'tokens' | 'roles'>

function checkSharedMailboxEmail(setMailbox: (mailbox: string) => void) {
    if (isOfficeInitialized() && Office.context.mailbox.item?.getSharedPropertiesAsync) {
        Office.context.mailbox.item?.getSharedPropertiesAsync?.(result => {
            if (result.status === Office.AsyncResultStatus.Failed) {
                console.debug(
                    'AuthorizationProvider:',
                    'checkSharedMailboxEmail: Error while detecting shared mailbox:',
                    result.error.code,
                    result.error.name,
                    result.error.message,
                )
                setMailbox(Office.context.mailbox.userProfile.emailAddress)
                return
            }
            console.debug('AuthorizationProvider:', 'checkSharedMailboxEmail: Shared mailbox detected: ', JSON.stringify(result.value))
            setMailbox(result.value.targetMailbox)
        })
    } else if (isOfficeInitialized()) {
        console.debug(
            'AuthorizationProvider:',
            "checkSharedMailboxEmail: No message selected or the message isn't part of a shared mailbox:",
            Office.context.mailbox.userProfile.emailAddress,
        )
        setMailbox(Office.context.mailbox.userProfile.emailAddress)
    }
}

const isBootstrapTokenFresh = (token: string | undefined): token is string => {
    if (!token) {
        return false
    }
    try {
        const decoded = jwtDecode<OfficeTokenPayload>(token)
        if (!decoded?.exp) {
            return false
        }
        return DateTime.fromSeconds(decoded.exp, { zone: 'UTC' }).toMillis() - DateTime.now().toUTC().toMillis() > BOOTSTRAP_EXPIRY_BUFFER_MS
    } catch (e) {
        console.error(e)
        return false
    }
}

const parseBootstrapToken = (
    token: string,
    user: AccountInfo,
    setUser: (token: AccountInfo) => void,
    bootstrapTokenRef: React.MutableRefObject<string | undefined>,
    setBootstrapTokenExpiresAt: (expires: DateTime) => void,
) => {
    let decodedToken = undefined
    try {
        decodedToken = token && jwtDecode<OfficeTokenPayload>(token)
    } catch (e) {
        console.error(e)
    }
    console.debug(
        DateTime.now().toUTC().toISO(),
        'AuthorizationProvider: parseBootstrapToken: using token:',
        decodedToken?.uti,
        '. Expires at ',
        decodedToken && DateTime.fromSeconds(decodedToken.exp, { zone: 'UTC' }).toISO(),
    )

    if (!decodedToken) {
        // Office.auth.getAccessToken can resolve with an empty string; storing it would make
        // acquireHosts/acquireTokens send `Authorization: Bearer ` to the router (HAM-1001,
        // "expected 3 parts, but got 0"). Keep the ref unchanged and schedule the short expiry
        // so the retry timeout triggers a fresh acquisition.
        setBootstrapTokenExpiresAt(DateTime.now().plus({ seconds: 3 }).toUTC())
        return
    }

    bootstrapTokenRef.current = token
    setBootstrapTokenExpiresAt(DateTime.fromSeconds(decodedToken.exp, { zone: 'UTC' }))

    if (isOfficeInitialized() && decodedToken && user?.localAccountId !== decodedToken.oid) {
        setUser({
            homeAccountId: `${decodedToken.oid}.${decodedToken.tid}`,
            environment: 'login.windows.net',
            tenantId: decodedToken.tid,
            username: decodedToken.preferred_username?.toLowerCase() || decodedToken.unique_name?.toLowerCase() || decodedToken.upn?.toLowerCase(),
            localAccountId: decodedToken.oid,
            name: decodedToken.name,
        })
    }
}

const getTokenExpiryDateTime = (response?: PartialServerAuthorizationTokenResponse): DateTime => {
    const token = response && jwtDecode<OfficeTokenPayload>(response?.access_token)
    if (token?.exp) {
        return DateTime.fromSeconds(token?.exp, { zone: 'UTC' })
    } else if (response) {
        console.warn('no response found', token?.uti)
    } else if (token) {
        console.warn('no expiry found in token', token?.uti)
    }
    return DateTime.now().toUTC()
}

const isTokensNotExpired = (response?: Tokens) => {
    return (
        isTokenNotExpired(response?.[SecurityAuthority.Hammock]) &&
        isTokenNotExpired(response?.[SecurityAuthority.Exchange]) &&
        isTokenNotExpired(response?.[SecurityAuthority.SharePoint]) &&
        isTokenNotExpired(response?.[SecurityAuthority.WebPubSub])
    )
}

export const isTokenNotExpired = (response?: PartialServerAuthorizationTokenResponse) => {
    if (!response) {
        return false
    }
    const exp = getTokenExpiryDateTime(response)
    const now = DateTime.now().toUTC()
    return exp.toMillis() - now.toMillis() - ACCESS_TOKEN_EXPIRY_BUFFER_MS > 0
}

const isTransientNetworkError = (error: unknown): boolean => {
    const axiosError = error as AxiosError & { cause?: { code?: string; message?: string } }
    const combinedErrorText = [axiosError?.code, axiosError?.name, axiosError?.message, axiosError?.cause?.code, axiosError?.cause?.message]
        .filter(Boolean)
        .join(' ')
        .toUpperCase()

    return combinedErrorText.includes('ERR_NETWORK') || combinedErrorText.includes('NETWORK ERROR')
}

const clearLocalStorageGlobals = () => {
    try {
        console.debug(DateTime.now().toUTC().toISO(), 'AuthorizationProvider: clearing global-auth localStorage keys')
        const keyPrefix = 'global-auth'
        for (let i = window.localStorage.length - 1; i >= 0; i--) {
            const key = window.localStorage.key(i)
            if (key && key.toLowerCase().startsWith(keyPrefix)) {
                window.localStorage.removeItem(key)
            }
        }
    } catch (e) {
        console.error(DateTime.now().toUTC().toISO(), 'AuthorizationProvider: clearing global-auth localStorage keys failed', e)
    }
}

/**
 * <p>This context is responsible for detecting the current user, the current mailbox and fetching a bootstrap token.</p>
 * <p>There are 2 situations:
 * - Standalone: The current mailbox is always the user's mailbox. No mailbox switch detection is performed.
 * - Outlook: The current mailbox is either the user's mailbox or a shared mailbox (info@ or support@).
 *   - Outlook will inform us of the current mailbox's e-mail address.
 *   - Mailbox switch detection is added, so Outlook will inform us of the current mailbox's e-mail address when switching between e-mail messages.
 *
 * </p>
 */
export function AuthorizationProvider(props: PropsWithChildren): ReactElement {
    const axios = useAxios()

    /**
     * The client responsible for communicating with MS to gain a bootstrapToken.
     */
    const [pca, setPca] = useState<IPublicClientApplication>()
    const [isPCAReady, setIsPCAReady] = useState<boolean>(false)

    /**
     * A list of variables required to log in / refresh tokens.
     */
    const [user, setUser] = useState<AccountInfo>()
    const [mailbox, setMailbox] = useState<string>()

    const bootstrapTokenRef = useRef<string>()
    const bootstrapTokenRequestRef = useRef<Promise<void>>()
    const [bootstrapTokenExpiresAt, setBootstrapTokenExpiresAt] = useState<DateTime>()
    const bootstrapTokenTimeoutRef = useRef<ReturnType<typeof setTimeout>>()

    const [hosts, setHosts] = useState<Host[]>()
    const [host, setHost] = useState<Host>()
    const hostsRequestRef = useRef<Promise<void>>()

    const [tokens, setTokens] = useState<Tokens>()
    const tokensRequestRef = useRef<Promise<void | Tokens>>()
    const [tokensExpiresAt, setTokensExpiresAt] = useState<DateTime>()
    const tokensTimeoutRef = useRef<ReturnType<typeof setTimeout>>()
    /**
     * Identifies the host that the currently held `tokens` were fetched for. Used together with
     * `host.RowKey` to detect that we are between a host switch and the arrival of new tokens.
     */
    const [tokensHostKey, setTokensHostKey] = useState<string>()
    /**
     * Monotonically increasing counter that bumps every time `tokens` is replaced with a fresh
     * server response. Exposed via the context as `tokensRevision`.
     */
    const [tokensRevision, setTokensRevision] = useState<number>(0)
    /**
     * Guards the one-time `Login.Authenticated` telemetry event for this page load.
     */
    const initialLoginTrackedRef = useRef(false)

    const bootstrapTokenValid = useMemo(() => {
        if (!bootstrapTokenRef.current) {
            return false
        }
        let decodedToken = undefined
        try {
            decodedToken = jwtDecode<OfficeTokenPayload>(bootstrapTokenRef.current)
        } catch (e) {
            console.error(e)
        }
        if (decodedToken && decodedToken.exp) {
            const exp = DateTime.fromSeconds(decodedToken.exp, { zone: 'UTC' })
            const now = DateTime.now().toUTC()
            return exp.toMillis() - now.toMillis() > BOOTSTRAP_EXPIRY_BUFFER_MS
        }

        return false
    }, [bootstrapTokenExpiresAt])

    const identified = useMemo(() => !!user && bootstrapTokenValid, [user, bootstrapTokenValid])

    const authenticated = useMemo(() => !!host && !!tokens && isTokensNotExpired(tokens), [host, tokens])

    const roles = useMemo<Role[]>(
        () =>
            tokens?.[SecurityAuthority.Hammock] ? jwtDecode<AzureTokenPayload>(tokens?.[SecurityAuthority.Hammock].access_token)?.roles || [] : [],
        [tokens],
    )

    /**
     * Stable identifier for the (user, environment) tuple. See `AuthorizationContextType.environmentKey`
     * for the contract. Defined only when tokens for the currently selected host are present and not
     * expired, so consumers can `useEffect(() => reload(), [environmentKey])` without racing token load.
     */
    const environmentKey = useMemo<string | undefined>(() => {
        if (!host || !user || !tokens) {
            return undefined
        }
        if (tokensHostKey !== host.RowKey) {
            return undefined
        }
        if (!isTokensNotExpired(tokens)) {
            return undefined
        }
        return `${host.RowKey}:${user.localAccountId}`
    }, [host, user, tokens, tokensHostKey])

    const { loaded: loadedFirstVisit, values: firstVisit } = useLocalStorage<boolean, boolean>({
        key: 'firstVisit',
        defaultValue: true,
        fromJson: values => values,
        toJson: values => values,
    })

    /**
     * In Outlook, scope `AuthenticationStorage` (and the derived `storagePrefix`) by the currently
     * signed-in Outlook account so switching between accounts in different tenants on Outlook for
     * Mac picks up the right cached identity instead of replaying the previous account's. The add-in
     * iframe reloads on account switch, so each mount sees the current account. `undefined` outside
     * Outlook leaves the storage under the default `'global'` key, preserving standalone-mode behavior.
     *
     * `userProfile.emailAddress` can lag behind `Office.onReady` on a cold cross-account reload: if we
     * captured it once at mount it would be `undefined`, silently routing add-in storage to the
     * standalone `'global'` bucket and preventing a warm restore. So we seed it if already available
     * and otherwise poll briefly until it appears; the `load` gate below blocks any `'global'`
     * read/write until it does.
     */
    const [outlookAccountKey, setOutlookAccountKey] = useState<string | undefined>(() =>
        isOfficeInitialized() ? Office.context.mailbox?.userProfile?.emailAddress?.toLowerCase() : undefined,
    )
    useEffect(() => {
        if (!isOfficeInitialized() || outlookAccountKey) {
            return undefined
        }
        let attempts = 0
        const interval = setInterval(() => {
            const email = Office.context.mailbox?.userProfile?.emailAddress?.toLowerCase()
            if (email || ++attempts >= 25) {
                clearInterval(interval)
                if (email) {
                    setOutlookAccountKey(email)
                }
            }
        }, 200)
        return () => clearInterval(interval)
    }, [outlookAccountKey])

    const {
        values: authenticationStorage,
        setValues: setAuthenticationStorage,
        loaded: authenticationStorageLoaded,
    } = useLocalStorage<AuthenticationStorageDto, AuthenticationStorageDto>({
        hostKey: outlookAccountKey,
        key: 'AuthenticationStorage',
        fromJson: values => values,
        toJson: values => values,
        defaultValue: undefined,
        // In the add-in, wait for the account email before touching localStorage so identity never
        // lands in the standalone `'global'` bucket. Standalone (no Office) loads immediately.
        load: !isOfficeInitialized() || outlookAccountKey !== undefined,
    })
    const [isUserHydrated, setIsUserHydrated] = useState<boolean>(false)

    /**
     * Stays `undefined` until we know which user owns the host storage:
     * - Outlook: `outlookAccountKey` is known from mount, so the prefix is `pathname-email`
     *   immediately and `AuthorizationHostStorage` hydrates without a key-change re-read.
     * - Standalone: wait for `homeAccountId` to hydrate from `AuthenticationStorage` (which itself
     *   needs `authenticationStorageLoaded`). Returning `undefined` here keeps `useLocalStorage`'s
     *   `load` gate closed and prevents orphan writes to `global-AuthorizationHostStorage` during
     *   the pre-login window (first visit, post-reset, or after a sign-out).
     */
    const storagePrefix = useMemo<string>(() => {
        const userPart = outlookAccountKey ?? (authenticationStorageLoaded ? authenticationStorage?.user?.homeAccountId : undefined)
        if (!userPart) {
            return undefined
        }
        return [window.location.pathname.replaceAll('/', ''), userPart].filter(Boolean).join('-')
    }, [authenticationStorageLoaded, authenticationStorage, outlookAccountKey])
    /**
     * During an account switch there is a window where `user` is already the new account (set by
     * `handleRedirectPromise`) while `authenticationStorage` — and therefore `storagePrefix` and the
     * loaded `authorizationHostStorage` values — still belong to the previous account. Hydrating or
     * persisting host storage in that window mixes the two accounts: the previous account's `host`
     * gets adopted (and persisted) for the new account, whose tokens request then 404s on a host it
     * has no access to (see logs/AuthorizationContext/190-standalone-random-login.log in the claude
     * repo). In Outlook mode `storagePrefix` is derived from `outlookAccountKey`, so it cannot lag.
     */
    const storageMatchesUser = outlookAccountKey !== undefined || authenticationStorage?.user?.homeAccountId === user?.homeAccountId
    const {
        values: authorizationHostStorage,
        setValues: setAuthorizationHostStorage,
        loaded: authorizationHostStorageLoaded,
    } = useLocalStorage<AuthorizationHostStorageDto, AuthorizationHostStorageDto>({
        hostKey: storagePrefix,
        key: 'AuthorizationHostStorage',
        fromJson: values => values,
        toJson: values => values,
        defaultValue: undefined,
        load: storagePrefix !== undefined,
    })
    const [isBootstrapHydrated, setIsBootstrapHydrated] = useState<boolean>(false)

    const tokensStoragePrefix = useMemo<string>(
        () => (!host ? undefined : [host?.RowKey, storagePrefix].filter(Boolean).join('-')),
        [host, storagePrefix],
    )
    const {
        values: authorizationTokensStorage,
        setValues: setAuthorizationTokensStorage,
        loaded: authorizationTokensStorageLoaded,
    } = useLocalStorage<AuthorizationTokensStorageDto, AuthorizationTokensStorageDto>({
        hostKey: tokensStoragePrefix,
        key: 'AuthorizationTokensStorage',
        fromJson: values => values,
        toJson: values => values,
        defaultValue: undefined,
        load: authorizationHostStorageLoaded && tokensStoragePrefix !== undefined,
    })
    const [isHydrated, setIsHydrated] = useState<boolean>(false)

    useMount(() => trackLoginPhase('started'))

    /**
     * Initialize the OAuth client. This Client will handle any login redirect when in standalone mode.
     * When in Outlook, this client is not used.
     */
    useMount(() => {
        PublicClientApplication.createPublicClientApplication({
            auth: {
                clientId: process.env.BOOTSTRAP_CLIENT_ID,
                authority: 'https://login.microsoftonline.com/common',
                navigateToLoginRequestUrl: false,
                onRedirectNavigate: url => {
                    console.debug(DateTime.now().toUTC().toISO(), 'onRedirectNavigate', url)
                    return true
                },
            },
            cache: {
                cacheLocation: BrowserCacheLocation.LocalStorage,
            },
            system: {
                tokenRenewalOffsetSeconds: 300,
                loggerOptions: {
                    loggerCallback: (_level: LogLevel, message: string) => {
                        console.debug(message)
                    },
                    logLevel: Number(process.env.MSAL_LOG_LEVEL),
                    piiLoggingEnabled: asBoolean(process.env.VERBOSE_ERRORS),
                },
            },
        }).then(client => {
            console.debug(DateTime.now().toUTC().toISO(), `AuthorizationProvider: pca init complete`)
            setPca(client)

            client
                .handleRedirectPromise()
                .then((result: AuthenticationResult) => {
                    console.debug(DateTime.now().toUTC().toISO(), 'AuthorizationProvider: handleRedirectPromise', JSON.stringify(result))
                    if (result) {
                        console.debug(DateTime.now().toUTC().toISO(), 'AuthorizationProvider: handleRedirectPromise setting account')
                        setUser(result.account)
                        parseBootstrapToken(result.accessToken, user, setUser, bootstrapTokenRef, setBootstrapTokenExpiresAt)
                        trackLoginPhase('bootstrap', { mode: 'msal-redirect' })
                        setIsUserHydrated(true)
                        setIsBootstrapHydrated(true)
                    } else {
                        const activeAccount = client.getActiveAccount()
                        const currentAccounts = client.getAllAccounts()
                        console.debug(
                            DateTime.now().toUTC().toISO(),
                            'AuthorizationProvider:',
                            `handleRedirectPromise activeAccount=${activeAccount?.username}, numberOfAccounts=${currentAccounts?.length}`,
                        )
                        if (activeAccount) {
                            setUser(activeAccount)
                            setIsUserHydrated(true)
                        } else if (currentAccounts && currentAccounts.length === 1) {
                            setUser(currentAccounts[0])
                            setIsUserHydrated(true)
                        }
                    }
                    setIsPCAReady(true)
                })
                .catch(e => {
                    console.debug(DateTime.now().toUTC().toISO(), 'AuthorizationProvider: handleRedirectPromise', e)
                    trackLoginFailure('redirect-callback', e, { mode: 'msal-redirect' })
                })
        })
    })

    /**
     * Add an Async Handler for Outlook which detects if the user switches to a shared mailbox.
     */
    useMount(() => {
        if (isOfficeInitialized()) {
            console.debug(DateTime.now().toUTC().toISO(), 'AuthorizationProvider:', Office.context.mailbox.userProfile)

            if (Office.context.mailbox.addHandlerAsync) {
                Office.context.mailbox.addHandlerAsync(
                    Office.EventType.ItemChanged,
                    () => {
                        console.debug(
                            DateTime.now().toUTC().toISO(),
                            'AuthorizationProvider:',
                            'Detected switch to mailbox item:',
                            Office.context.mailbox.item?.subject,
                        )
                        checkSharedMailboxEmail(setMailbox)
                    },
                    result => {
                        if (result.status === Office.AsyncResultStatus.Failed) {
                            console.debug(
                                DateTime.now().toUTC().toISO(),
                                'AuthorizationProvider:',
                                'Error in ItemChanged handler:',
                                result.error.code,
                                result.error.name,
                                result.error.message,
                            )
                        }
                    },
                )

                return () => Office.context.mailbox.removeHandlerAsync(Office.EventType.ItemChanged)
            } else {
                console.error('base.error.outdated_outlook')
            }
        }

        return undefined
    })

    /**
     * Acquire a bootstrap token. The request is stored in a State to prevent multiple concurrent requests.
     *
     * This function has 2 flows:
     * - Outlook: Only silently request an access token.
     * - Standalone: Attempt to silently acquire a bootstrap token and fall back to a redirect if this fails.
     */
    const acquireBootstrapTokenSilent = (): Promise<void> => {
        if (bootstrapTokenRequestRef.current) {
            return bootstrapTokenRequestRef.current
        }

        let request: Promise<void>
        if (isOfficeInitialized()) {
            console.debug(DateTime.now().toUTC().toISO(), 'AuthorizationProvider: getBootstrapToken (office) started')
            request = Office.auth
                .getAccessToken({ allowConsentPrompt: true, allowSignInPrompt: true })
                .then(result => {
                    parseBootstrapToken(result, user, setUser, bootstrapTokenRef, setBootstrapTokenExpiresAt)
                    trackLoginPhase('bootstrap', { mode: 'office-sso' })
                })
                .catch(error => {
                    console.debug(DateTime.now().toUTC().toISO(), `AuthorizationProvider: getBootstrapToken due to error: ${JSON.stringify(error)}`)
                    trackLoginFailure('bootstrap', error, { mode: 'office-sso' })
                    setBootstrapTokenExpiresAt(DateTime.now().plus({ minute: 1 }).toUTC())
                })
                .finally(() => {
                    bootstrapTokenRequestRef.current = undefined
                    console.debug(DateTime.now().toUTC().toISO(), 'AuthorizationProvider: getBootstrapToken (office) finished')
                })
        } else if (isPCAReady && user) {
            console.debug(DateTime.now().toUTC().toISO(), 'AuthorizationProvider: getBootstrapToken (silent) started')
            request = pca
                .acquireTokenSilent({
                    authenticationScheme: 'Bearer',
                    account: user,
                    scopes: AUTHENTICATION_SCOPES,
                })
                .then(result => {
                    parseBootstrapToken(result.accessToken, user, setUser, bootstrapTokenRef, setBootstrapTokenExpiresAt)
                    trackLoginPhase('bootstrap', { mode: 'msal-silent' })
                })
                .catch(error => {
                    console.debug(
                        DateTime.now().toUTC().toISO(),
                        `AuthorizationProvider: getBootstrapToken (silent) due to error: ${JSON.stringify(error)}`,
                    )
                    trackLoginFailure('bootstrap', error, { mode: 'msal-silent', willRedirect: true })
                    flushLoginTelemetry()
                    // Clear the timeout to prevent a racing retry while redirect is in progress
                    if (bootstrapTokenTimeoutRef.current) {
                        clearTimeout(bootstrapTokenTimeoutRef.current)
                        bootstrapTokenTimeoutRef.current = undefined
                    }
                    // Clear the ref so the redirect guard doesn't block, and clear the timeout to prevent a racing retry
                    bootstrapTokenRequestRef.current = undefined
                    return acquireBootstrapTokenRedirect()
                })
                .finally(() => {
                    bootstrapTokenRequestRef.current = undefined
                    console.debug(DateTime.now().toUTC().toISO(), 'AuthorizationProvider: getBootstrapToken (silent) finished')
                })
        } else {
            request = acquireBootstrapTokenRedirect()
        }

        bootstrapTokenRequestRef.current = request
        return request
    }

    /**
     * Invoke an HTTP redirect to get a user and bootstrap token.
     */
    const acquireBootstrapTokenRedirect = (): Promise<void> => {
        if (bootstrapTokenRequestRef.current) {
            return bootstrapTokenRequestRef.current
        }

        console.debug(DateTime.now().toUTC().toISO(), 'AuthorizationProvider: getBootstrapToken (redirect) started')

        const request: Promise<void> = pca
            .acquireTokenRedirect({
                authenticationScheme: 'Bearer',
                account: user,
                scopes: AUTHENTICATION_SCOPES,
                prompt: pca.getAllAccounts()?.length > 0 ? undefined : 'select_account',
            })
            .catch(error => {
                console.debug(
                    DateTime.now().toUTC().toISO(),
                    `AuthorizationProvider: getBootstrapToken (redirect) due to error: ${JSON.stringify(error)}`,
                )
                trackLoginFailure('bootstrap', error, { mode: 'msal-redirect' })
                throw error
            })
            .finally(() => {
                bootstrapTokenRequestRef.current = undefined
                console.debug(DateTime.now().toUTC().toISO(), 'AuthorizationProvider: getBootstrapToken (redirect) finished')
            })

        bootstrapTokenRequestRef.current = request
        return request
    }

    /**
     * Guards `handleRouterUnauthorized` against a refresh loop: holds the `uti` of the last
     * bootstrap token the router rejected with a 401. If MSAL/Office hands back that same token
     * again (e.g. the client clock runs far ahead, so the stale token passes the local freshness
     * check), we refresh at most once per token instead of hammering the router.
     */
    const last401RejectedUtiRef = useRef<string>()

    /**
     * The router answered 401 (HAM-1007/HAM-1024): it rejected the bootstrap assertion as (nearly)
     * expired. Drop the bootstrap token so `bootstrapTokenValid` flips to false, the bootstrap
     * effect silently fetches a fresh one, and the tokens effect then retries — reusing the
     * existing self-healing machinery instead of an ad-hoc retry.
     *
     * @returns true when a refresh was initiated, false when this token was already rejected once.
     */
    const handleRouterUnauthorized = (source: string): boolean => {
        let uti = undefined
        try {
            uti = bootstrapTokenRef.current && jwtDecode<OfficeTokenPayload>(bootstrapTokenRef.current).uti
        } catch (e) {
            console.error(e)
        }
        if (uti && uti === last401RejectedUtiRef.current) {
            console.debug(
                DateTime.now().toUTC().toISO(),
                `AuthorizationProvider: ${source} got 401 for bootstrap token`,
                uti,
                'again; not refreshing a second time',
            )
            return false
        }
        console.debug(
            DateTime.now().toUTC().toISO(),
            `AuthorizationProvider: ${source} got 401; dropping bootstrap token`,
            uti,
            'to force a silent refresh',
        )
        last401RejectedUtiRef.current = uti
        bootstrapTokenRef.current = undefined
        setBootstrapTokenExpiresAt(DateTime.now().toUTC())
        return true
    }

    /**
     * Fetch available hosts when bootstrapToken is available, but we have not yet set a host.
     *
     */
    const acquireHosts = (): Promise<void> => {
        if (hostsRequestRef.current) {
            return hostsRequestRef.current
        }

        let decodedBootstrapToken = undefined
        try {
            decodedBootstrapToken = jwtDecode<OfficeTokenPayload>(bootstrapTokenRef.current)
        } catch (e) {
            console.error(e)
        }
        console.debug(
            DateTime.now().toUTC().toISO(),
            'AuthorizationProvider: acquireHosts started',
            host?.RowKey,
            '. bootstrapToken:',
            decodedBootstrapToken?.uti,
        )

        const request: Promise<void> = axios
            .get<Host[]>(`/api/host`, {
                baseURL: process.env.ROUTER_HOST,
                headers: { Authorization: `Bearer ${bootstrapTokenRef.current}`, SuppressErrors: 'true' },
            })
            .then(response => {
                setHosts(response.data)
                trackLoginPhase('hosts', { hostCount: response.data?.length ?? 0, autoSelected: response.data?.length === 1 })
                if (response.data?.length === 1) {
                    setHost(response.data[0])
                    // Skip the auto-tokens commit when an explicit acquireTokens is already running for this host:
                    // the in-flight fetch will deliver a fresh value. Committing stale auto-tokens here scheduled a
                    // near-now 3s expiry timeout that could double-fire acquireTokens on slow networks (see
                    // tmp/900-standalone-first-login-of-day.log).
                    if (response.data[0].tokens && !tokensRequestRef.current) {
                        setTokens(response.data[0].tokens)
                        setTokensHostKey(response.data[0].RowKey)
                        setTokensRevision(r => r + 1)
                        markTokensFetched()

                        // Take the earliest expiry across the tokens we actually received. `SecurityAuthority`
                        // is a string enum, so iterate its *values* (`'hammock'`, …) — the keys `tokens` is
                        // keyed by. Iterating with `for…in` yields the enum *names* (`'Hammock'`, …), which miss
                        // every lookup and drag the expiry down to `now` via getTokenExpiryDateTime's fallback.
                        // Skip absent tokens for the same reason.
                        let expireDateTime: DateTime | undefined = undefined
                        for (const authority of Object.values(SecurityAuthority)) {
                            const token = response.data[0].tokens?.[authority]
                            if (!token) {
                                continue
                            }
                            const dateTime = getTokenExpiryDateTime(token)
                            if (!expireDateTime || expireDateTime > dateTime) {
                                expireDateTime = dateTime
                            }
                        }
                        if (expireDateTime) {
                            console.debug(
                                DateTime.now().toUTC().toISO(),
                                'AuthorizationProvider: setting tokensExpiresAt to ',
                                expireDateTime.toISO(),
                            )
                            setTokensExpiresAt(expireDateTime.plus({ seconds: 3 }))
                        }
                    }
                } else if (!response.data?.some(h => h.RowKey === host?.RowKey)) {
                    // The selected host is not in the list this user can access. Also clear `host`
                    // itself, otherwise the tokens-acquisition effect keeps hammering /api/tokens on
                    // a host that will keep answering 404.
                    setHost(undefined)
                    setTokens(undefined)
                    setTokensExpiresAt(undefined)
                    setTokensHostKey(undefined)
                }
            })
            .catch(error => {
                if (error.response?.status === 404) {
                    setHosts([])
                } else if (error.response?.status === 401) {
                    // HAM-1007/HAM-1024: the router rejected the bootstrap assertion as (nearly)
                    // expired; refresh it silently and let the hosts effect retry.
                    handleRouterUnauthorized('acquireHosts')
                }
                console.error('AuthorizationProvider: error', error)
                trackLoginFailure('hosts', error)
            })
            .finally(() => {
                hostsRequestRef.current = undefined
                console.debug(DateTime.now().toUTC().toISO(), 'AuthorizationProvider: acquireHosts finished')
            })

        hostsRequestRef.current = request
        return request
    }

    const acquireTokens = (): Promise<void | Tokens> => {
        if (tokensRequestRef.current) {
            return tokensRequestRef.current
        }

        // Snapshot the host we are fetching tokens for. If the user switches host while this request
        // is in flight, we discard the response rather than committing tokens that belong to the
        // previous host.
        const acquiringForHost = host
        if (!acquiringForHost) {
            return Promise.resolve()
        }

        let decodedBootstrapToken = undefined
        try {
            decodedBootstrapToken = jwtDecode<OfficeTokenPayload>(bootstrapTokenRef.current)
        } catch (e) {
            console.error(e)
        }
        console.debug(
            DateTime.now().toUTC().toISO(),
            'AuthorizationProvider: acquireTokens started',
            acquiringForHost.RowKey,
            '. bootstrapToken: ',
            decodedBootstrapToken?.uti,
        )

        const request: Promise<void | Tokens> = axios
            .get<Tokens>(`/api/tokens/${acquiringForHost.RowKey}`, {
                baseURL: process.env.ROUTER_HOST,
                headers: { Authorization: `Bearer ${bootstrapTokenRef.current}`, SuppressErrors: 'true' },
            })
            .then(response => {
                let decodedToken = undefined
                try {
                    decodedToken = jwtDecode<OfficeTokenPayload>(response.data?.[SecurityAuthority.Hammock]?.access_token)
                } catch (e) {
                    console.error(e)
                }
                console.debug(
                    DateTime.now().toUTC().toISO(),
                    'AuthorizationProvider: acquireTokens. New HAMMOCK token:',
                    decodedToken?.uti,
                    '. Expires at ',
                    getTokenExpiryDateTime(response.data?.[SecurityAuthority.Hammock]).toISO(),
                )
                setTokens(response.data)
                setTokensHostKey(acquiringForHost.RowKey)
                setTokensRevision(r => r + 1)
                markTokensFetched()
                setTokensExpiresAt(getTokenExpiryDateTime(response.data?.[SecurityAuthority.WebPubSub]))
                return response.data
            })
            .catch(error => {
                console.error('AuthorizationProvider: error', error)
                trackLoginFailure('tokens', error, { host: acquiringForHost.RowKey, transientNetwork: isTransientNetworkError(error) })
                if (error.response?.status === 404) {
                    // The host does not exist (anymore) for this user — retrying is pointless. This
                    // happens when a stale host survives an account switch or is revoked server-side.
                    // Clear the host so the hosts-acquisition effect refetches the list this user can
                    // actually access, which also overwrites the stale persisted value.
                    console.debug(
                        DateTime.now().toUTC().toISO(),
                        'AuthorizationProvider: acquireTokens got 404; clearing host',
                        acquiringForHost.RowKey,
                    )
                    setHost(undefined)
                    setHosts(undefined)
                    setTokens(undefined)
                    setTokensExpiresAt(undefined)
                    setTokensHostKey(undefined)
                } else if (error.response?.status === 401) {
                    // HAM-1007/HAM-1024: the router rejected the bootstrap assertion as (nearly)
                    // expired; refresh it silently and let the tokens effect retry.
                    handleRouterUnauthorized('acquireTokens')
                } else if (isTransientNetworkError(error)) {
                    setTokensExpiresAt(DateTime.now().plus({ seconds: 5 }).toUTC())
                }
            })
            .finally(() => {
                tokensRequestRef.current = undefined
                console.debug(DateTime.now().toUTC().toISO(), 'AuthorizationProvider: acquireTokens finished')
            })

        tokensRequestRef.current = request
        return request
    }

    /**
     * ------------------
     */

    /**
     * Update user, mailbox, bootstrap token, hosts and host with information from the cookies, if present.
     */
    useEffect(() => {
        if (!isPCAReady) {
            return
        }
        if (!loadedFirstVisit || firstVisit) {
            console.debug(DateTime.now().toUTC().toISO(), 'AuthorizationProvider: not hydrating user because this is the first visit.')
            return
        }

        if (authenticationStorageLoaded) {
            console.debug(DateTime.now().toUTC().toISO(), 'AuthorizationProvider: hydrating authenticationStorage')
            setUser(user ?? authenticationStorage?.user)
            setMailbox(mailbox ?? authenticationStorage?.mailbox)
        }
        setIsUserHydrated(authenticationStorageLoaded)
    }, [isPCAReady, authenticationStorageLoaded, firstVisit])

    useEffect(() => {
        if (!isUserHydrated) {
            return
        }
        setAuthenticationStorage({ identified, authenticated, user, mailbox })
    }, [isUserHydrated, authenticationStorageLoaded, user, mailbox])

    useEffect(() => {
        if (!isUserHydrated) {
            return
        }
        // Without a user there's no user-scoped host storage to hydrate, but downstream effects
        // (notably the bootstrap-acquisition effect that triggers the MSAL redirect in standalone
        // mode) still need to run, so mark hydration complete immediately. Never flip
        // `isBootstrapHydrated` back to false once true — that would gate-block the post-login
        // window where `user` is set but `authorizationHostStorageLoaded` hasn't caught up yet.
        if (!user) {
            setIsBootstrapHydrated(true)
            return
        }
        if (!storageMatchesUser) {
            // Account switch in progress: the loaded storage belongs to the previous account. Skip
            // hydration entirely — `acquireHosts` will fetch the new account's hosts once `host` is
            // still unset. Deliberately not re-run when `authenticationStorage` catches up: by then
            // the storage values may still be the previous account's (the per-key reload lands a
            // render later), so re-hydrating here would reopen the race.
            console.debug(
                DateTime.now().toUTC().toISO(),
                'AuthorizationProvider: skipping authorizationHostStorage hydration; storage belongs to a different account',
            )
            setIsBootstrapHydrated(true)
            return
        }
        if (authorizationHostStorageLoaded) {
            console.debug(DateTime.now().toUTC().toISO(), 'AuthorizationProvider: hydrating authorizationHostStorage')
            const storedBootstrapToken = bootstrapTokenRef.current ?? authorizationHostStorage?.bootstrapToken
            if (isBootstrapTokenFresh(storedBootstrapToken)) {
                parseBootstrapToken(storedBootstrapToken, user, setUser, bootstrapTokenRef, setBootstrapTokenExpiresAt)
                trackLoginPhase('bootstrap', { mode: 'storage' })
            } else if (storedBootstrapToken) {
                console.debug(DateTime.now().toUTC().toISO(), 'AuthorizationProvider: discarding stale stored bootstrapToken')
            }
            setHosts(hosts ?? authorizationHostStorage?.hosts)
            setHost(host ?? authorizationHostStorage?.host)
            setIsBootstrapHydrated(true)
        }
    }, [isUserHydrated, authorizationHostStorageLoaded, user])

    useEffect(() => {
        // Skip when `storagePrefix` is undefined (e.g. just after `resetUser` clears the user).
        // `useLocalStorage` falls back to `hostPrefix='global'` for an undefined `hostKey`, so writing
        // here would persist to `global-AuthorizationHostStorage` instead of a per-user key.
        // Also skip while `storagePrefix` still belongs to the previous account during an account
        // switch: writing then would persist the new account's bootstrapToken under the previous
        // account's key.
        if (!isBootstrapHydrated || storagePrefix === undefined || !storageMatchesUser) {
            return
        }
        setAuthorizationHostStorage({ bootstrapToken: bootstrapTokenRef.current, hosts, host })
    }, [isBootstrapHydrated, authorizationHostStorageLoaded, bootstrapTokenExpiresAt, hosts, host, storagePrefix])

    /**
     * Once the PCA is ready and the cookies are hydrated, hydrate the localstorage.
     */
    useEffect(() => {
        if (!isBootstrapHydrated) {
            return
        }
        if (authorizationTokensStorageLoaded) {
            console.debug(DateTime.now().toUTC().toISO(), 'AuthorizationProvider: hydrating authorizationTokensStorage')
            const hydratedTokens = tokens ?? authorizationTokensStorage?.tokens
            if (hydratedTokens && isTokensNotExpired(hydratedTokens)) {
                setTokens(hydratedTokens)
                setTokensExpiresAt(getTokenExpiryDateTime(hydratedTokens?.[SecurityAuthority.WebPubSub]))
                // The tokens localStorage key is scoped by host.RowKey via tokensStoragePrefix, so any
                // hydrated tokens are by construction for the currently selected host.
                if (host?.RowKey) {
                    setTokensHostKey(host.RowKey)
                }
            } else if (hydratedTokens) {
                console.debug(DateTime.now().toUTC().toISO(), 'AuthorizationProvider: discarding stale stored tokens')
            }
        }
        setIsHydrated(authorizationTokensStorageLoaded)
    }, [isBootstrapHydrated, authorizationTokensStorageLoaded])

    useEffect(() => {
        // Same guard as the host-storage persist effect: when `tokensStoragePrefix` is undefined
        // (no host selected, e.g. just after logout) the underlying `useLocalStorage` would write to
        // `global-AuthorizationTokensStorage`. Skip until we have a real per-(host, user) prefix.
        if (!isHydrated || tokensStoragePrefix === undefined) {
            return
        }
        setAuthorizationTokensStorage({ tokens, roles })
    }, [isHydrated, authorizationTokensStorageLoaded, tokens, tokensStoragePrefix])

    /**
     * ------------------
     * Initialize all necessary fields after the localstorage and cookies have been hydrated.
     */

    /**
     * Check if the user is currently in a shared mailbox.
     */
    useEffect(() => {
        if (isOfficeInitialized()) {
            checkSharedMailboxEmail(setMailbox)
        } else {
            setMailbox(user?.username)
        }
    }, [authenticationStorageLoaded, user])

    /**
     * Acquire a bootstraptoken.
     */
    useEffect(() => {
        if (isBootstrapHydrated && (isOfficeInitialized() || user) && !bootstrapTokenValid) {
            acquireBootstrapTokenSilent()
        } else if (isBootstrapHydrated && !isOfficeInitialized() && !user) {
            acquireBootstrapTokenRedirect()
        }
    }, [isBootstrapHydrated, user, bootstrapTokenValid, loadedFirstVisit, firstVisit])

    /**
     * Once we have a bootstrap token but no host has been selected, fetch available hosts.
     */
    useEffect(() => {
        if (isBootstrapHydrated && bootstrapTokenValid && !host) {
            acquireHosts()
        }
    }, [isBootstrapHydrated, bootstrapTokenValid, host])

    /**
     * Set the expiry timeout for the bootstrap token. Use a minimum of 3 seconds and a maximum of 30 minutes.
     */
    useEffect(() => {
        if (isBootstrapHydrated && bootstrapTokenExpiresAt) {
            console.debug(
                DateTime.now().toUTC().toISO(),
                'AuthorizationProvider: bootstrapTokenExpiresAt changed to',
                bootstrapTokenExpiresAt.toJSON(),
            )

            const timer = Math.min(Math.max(bootstrapTokenExpiresAt.toMillis() - DateTime.now().toUTC().toMillis(), 3_000), 1_800_000)
            console.debug(DateTime.now().toUTC().toISO(), 'AuthorizationProvider: Setting timeout for bootstrapToken in', timer, 'milliseconds.')

            if (bootstrapTokenTimeoutRef.current) {
                clearTimeout(bootstrapTokenTimeoutRef.current)
                bootstrapTokenTimeoutRef.current = undefined
            }
            bootstrapTokenTimeoutRef.current = setTimeout(() => acquireBootstrapTokenSilent(), timer)
            return () => {
                console.debug(DateTime.now().toUTC().toISO(), 'AuthorizationProvider: clearing timeout for bootstrapToken')
                if (bootstrapTokenTimeoutRef.current) {
                    clearTimeout(bootstrapTokenTimeoutRef.current)
                    bootstrapTokenTimeoutRef.current = undefined
                }
            }
        } else if (bootstrapTokenTimeoutRef.current) {
            console.debug(
                DateTime.now().toUTC().toISO(),
                'AuthorizationProvider: bootstrapTokenExpiresAt changed to undefined, clearing timeout for bootstrapToken',
            )
            clearTimeout(bootstrapTokenTimeoutRef.current)
            bootstrapTokenTimeoutRef.current = undefined
        }
        return () => {}
    }, [isBootstrapHydrated, bootstrapTokenExpiresAt])

    /**
     * Fetch the tokens when we have a bootstrap token but no valid pub sub token.
     *
     * `tokens` is in the deps because the host-change effect below clears tokens to `undefined`
     * when the user switches host. Without `tokens` here, that clear would not retrigger this
     * effect (host didn't change in that follow-up render) and the user would be stuck without
     * fresh tokens for the new host.
     */
    useEffect(() => {
        if (isHydrated && bootstrapTokenValid && host && !isTokensNotExpired(tokens)) {
            acquireTokens()
        } else if (isHydrated && !bootstrapTokenValid) {
            console.debug(DateTime.now().toUTC().toISO(), 'AuthorizationProvider: bootstrapToken is undefined')
        } else if (isHydrated && !host) {
            console.debug(DateTime.now().toUTC().toISO(), 'AuthorizationProvider: host is undefined')
        }
    }, [isHydrated, bootstrapTokenValid, host, tokens])

    /**
     * When the host changes (including to undefined on logout), drop any tokens that were loaded
     * for a different host. This guarantees that downstream consumers observing `environmentKey`
     * see it flip to `undefined` until tokens for the new host arrive, instead of briefly seeing
     * the new host paired with the previous host's tokens.
     */
    useEffect(() => {
        if (tokensHostKey && tokensHostKey !== host?.RowKey) {
            console.debug(DateTime.now().toUTC().toISO(), 'AuthorizationProvider: host changed; dropping tokens that belonged to', tokensHostKey)
            setTokens(undefined)
            setTokensExpiresAt(undefined)
            setTokensHostKey(undefined)
        }
    }, [host?.RowKey])

    /**
     * Set the expiry timeout for the tokens. Use a minimum of 3 seconds and a maximum of 25 minutes.
     * Also gated on `bootstrapTokenValid`: without a fresh bootstrap, the timer's `acquireTokens`
     * call would hit /api/tokens with a stale Authorization header and produce a guaranteed 401
     * (HAM-1007).
     */
    useEffect(() => {
        if (isHydrated && tokensExpiresAt && bootstrapTokenValid) {
            console.debug(DateTime.now().toUTC().toISO(), 'AuthorizationProvider: tokensExpiresAt changed to', tokensExpiresAt.toJSON())

            const timer = Math.min(Math.max(tokensExpiresAt.toMillis() - DateTime.now().toUTC().toMillis(), 3_000), 1_500_000)
            console.debug(DateTime.now().toUTC().toISO(), 'AuthorizationProvider: Setting timeout for tokens in', timer, 'milliseconds.')

            if (tokensTimeoutRef.current) {
                clearTimeout(tokensTimeoutRef.current)
                tokensTimeoutRef.current = undefined
            }
            tokensTimeoutRef.current = setTimeout(() => (host ? acquireTokens() : Promise.resolve()), timer)
            return () => {
                console.debug(DateTime.now().toUTC().toISO(), 'AuthorizationProvider: clearing timeout for tokens')
                if (tokensTimeoutRef.current) {
                    clearTimeout(tokensTimeoutRef.current)
                    tokensTimeoutRef.current = undefined
                }
            }
        } else if (tokensTimeoutRef.current) {
            console.debug(DateTime.now().toUTC().toISO(), 'AuthorizationProvider: tokensExpiresAt changed to undefined, clearing timeout for tokens')
            clearTimeout(tokensTimeoutRef.current)
            tokensTimeoutRef.current = undefined
        }
        return () => {}
    }, [isHydrated, host, tokensExpiresAt, bootstrapTokenValid])

    /**
     * Mark the initial login as complete. After this, silent refresh cycles no longer emit
     * success telemetry and their failures are tagged with trigger 'refresh' (see loginTelemetry.ts).
     */
    useEffect(() => {
        if (authenticated && !initialLoginTrackedRef.current) {
            initialLoginTrackedRef.current = true
            trackLoginPhase('authenticated', { host: host?.RowKey })
            markInitialLoginComplete()
        }
    }, [authenticated])

    useEffect(() => {
        const handleOffline = () => {
            console.debug(DateTime.now().toUTC().toISO(), 'AuthorizationProvider: window/tab went offline')
            clearTimeout(tokensTimeoutRef.current)
            clearTimeout(bootstrapTokenTimeoutRef.current)
        }

        const handleOnline = () => {
            console.debug(DateTime.now().toUTC().toISO(), 'AuthorizationProvider: window/tab went online')
            setTokensExpiresAt(tokensExpiresAt)
            setBootstrapTokenExpiresAt(bootstrapTokenExpiresAt)
        }

        window.addEventListener('offline', handleOffline)
        window.addEventListener('online', handleOnline)
        window.addEventListener('visibilitychange', handleOnline)

        return () => {
            window.removeEventListener('offline', handleOffline)
            window.removeEventListener('online', handleOnline)
            window.removeEventListener('visibilitychange', handleOnline)
        }
    }, [])

    const updateTokens = (authority: SecurityAuthority): Promise<ServerAuthorizationTokenResponse | WebPubSubToken> => {
        let request = Promise.resolve()

        if (!isBootstrapTokenFresh(bootstrapTokenRef.current)) {
            request = request.then(() => acquireBootstrapTokenSilent())
        }
        if (!hosts || !host) {
            // Re-check freshness: acquireBootstrapTokenSilent swallows failures (the Office flow
            // resolves after an error), so the ref may still hold no usable token here.
            request = request.then(() => {
                if (!isBootstrapTokenFresh(bootstrapTokenRef.current)) {
                    console.debug(
                        DateTime.now().toUTC().toISO(),
                        'AuthorizationProvider: skipping acquireHosts; bootstrap acquisition did not yield a fresh token',
                    )
                    return Promise.resolve()
                }
                return acquireHosts()
            })
        }

        return request
            .then(() => (host && isBootstrapTokenFresh(bootstrapTokenRef.current) ? acquireTokens() : Promise.resolve()))
            .then(tokens => tokens?.[authority])
    }

    const getBaseUrl = (destination: Destination = Destination.Hammock): string | undefined => {
        if (!host) {
            return undefined
        }

        switch (destination) {
            case Destination.SharePoint:
            case Destination.Exchange:
                return graphBaseUrl
            case Destination.Documents:
                return `${graphBaseUrl}/drives/${host?.DriveId}`
            case Destination.ReplicationEmail:
                return `${graphBaseUrl}/users/${host?.ReplicationMail}`
            case Destination.Email:
                return `${graphBaseUrl}/${mailbox ? 'users/' + mailbox : 'me'}`
            case Destination.PrivateEmail:
                return `${graphBaseUrl}/me`
            case Destination.Hammock:
            default:
                return host?.Backend
        }
    }

    const resetUser = () => {
        //Reset both to force both Standalone and Outlook to refresh user and bootstrap token.
        setUser(undefined)
        bootstrapTokenRef.current = undefined
        setBootstrapTokenExpiresAt(undefined)
        setAuthenticationStorage(undefined)
        setHost(undefined)
        // Deliberately do NOT call setAuthorizationHostStorage(undefined) here: at this point
        // storagePrefix still resolves to the outgoing user's per-user key, so the write would
        // persist the literal value "undefined" under that key and force `acquireHosts` to run
        // on the next re-login as the same user. The persist effect's `storagePrefix === undefined`
        // guard prevents any spurious follow-up writes once the state cascade settles.
        clearLocalStorageGlobals()
        pca.clearCache({ account: user })
            .then(() => pca.setActiveAccount(null))
            .then(() => window.location.reload())
    }

    return (
        <AuthorizationContext.Provider
            value={{
                identified,
                authenticated,
                user,
                mailbox,
                bootstrapToken: bootstrapTokenRef.current,
                hosts,
                host,
                tokens,
                roles,
                environmentKey,
                tokensRevision,
                updateTokens,
                setHost: (newHost: Host) => {
                    if (newHost !== undefined) {
                        if (host && newHost.RowKey !== host.RowKey) {
                            // Mid-session environment switch: the hash still mirrors the previous
                            // host's page. Reset it so the remounted Router restores the new
                            // host's own last visited page instead.
                            window.location.hash = '/'
                        }
                        setHost(newHost)
                    }
                },
                getBaseUrl,
                reset: resetUser,
            }}
        >
            {props.children}
        </AuthorizationContext.Provider>
    )
}

// noinspection JSUnusedGlobalSymbols
export default AuthorizationContext
